import React, { useState, FC, JSX, ChangeEvent, FormEvent } from 'react';
import Button from './Button';
import { SearchBarProps, SearchFilters } from '../../types/components.d';

const SearchBar: FC<SearchBarProps> = ({ 
  onSearch, 
  placeholder = "Search jobs, companies, or keywords...",
  showFilters = true,
  className = "" 
}): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filters, setFilters] = useState<SearchFilters>({
    location: '',
    jobType: '',
    experience: '',
    salary: ''
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState<boolean>(false);

  const handleSearch = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    onSearch({
      term: searchTerm,
      filters
    });
  };

  const handleFilterChange = (key: keyof SearchFilters, value: string): void => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      location: '',
      jobType: '',
      experience: '',
      salary: ''
    });
    setSearchTerm('');
  };

  return (
    <div className={`bg-white rounded-2xl shadow-soft p-6 ${className}`}>
      <form onSubmit={handleSearch} className="space-y-4">
        {/* Main Search Input */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={placeholder}
            className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen transition-all duration-300 bg-white text-lg"
          />
          <Button
            type="submit"
            className="absolute right-2 top-2 bottom-2 px-6"
          >
            Search
          </Button>
        </div>

        {/* Quick Filters */}
        {showFilters && (
          <div className="flex flex-wrap gap-3">
            <select
              value={filters.jobType}
              onChange={(e) => handleFilterChange('jobType', e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen bg-white"
            >
              <option value="">Job Type</option>
              <option value="full-time">Full Time</option>
              <option value="part-time">Part Time</option>
              <option value="contract">Contract</option>
              <option value="internship">Internship</option>
              <option value="remote">Remote</option>
            </select>

            <select
              value={filters.experience}
              onChange={(e) => handleFilterChange('experience', e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen bg-white"
            >
              <option value="">Experience</option>
              <option value="entry">Entry Level</option>
              <option value="mid">Mid Level</option>
              <option value="senior">Senior Level</option>
              <option value="executive">Executive</option>
            </select>

            <input
              type="text"
              value={filters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
              placeholder="Location"
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen bg-white"
            />

            <input
              type="text"
              value={filters.salary}
              onChange={(e) => handleFilterChange('salary', e.target.value)}
              placeholder="Min Salary"
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen bg-white"
            />

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            >
              {showAdvancedFilters ? 'Hide' : 'More'} Filters
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearFilters}
            >
              Clear All
            </Button>
          </div>
        )}

        {/* Advanced Filters */}
        {showAdvancedFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-xl">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company Size</label>
              <select
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen bg-white"
              >
                <option value="">Any Size</option>
                <option value="startup">Startup (1-50)</option>
                <option value="small">Small (51-200)</option>
                <option value="medium">Medium (201-1000)</option>
                <option value="large">Large (1000+)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Industry</label>
              <select
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen bg-white"
              >
                <option value="">Any Industry</option>
                <option value="technology">Technology</option>
                <option value="finance">Finance</option>
                <option value="healthcare">Healthcare</option>
                <option value="education">Education</option>
                <option value="retail">Retail</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Posted Date</label>
              <select
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen bg-white"
              >
                <option value="">Any Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="3months">Last 3 Months</option>
              </select>
            </div>
          </div>
        )}

        {/* Search Suggestions */}
        <div className="flex flex-wrap gap-2">
          <span className="text-sm text-gray-500">Popular searches:</span>
          {['React Developer', 'Data Scientist', 'Product Manager', 'UX Designer', 'DevOps Engineer'].map((term) => (
            <button
              key={term}
              type="button"
              onClick={() => setSearchTerm(term)}
              className="px-3 py-1 bg-primary-teaGreen text-primary-black rounded-full text-sm hover:bg-primary-lightGreen transition-colors duration-300"
            >
              {term}
            </button>
          ))}
        </div>
      </form>
    </div>
  );
};

export default SearchBar;
